import { Message } from "../layouts/AskDatatram/ChatInterface";
import { getTime } from "../layouts/AskDatatram/ChatHandler";
import {
  sendMessageToAPI,
  sendImageMessageToAPI,
  fetchChatSuggestions,
  ChatbotQueryLimitReachedError,
} from "src/services/ChatbotAPI";
import { sendCampaignEmail } from "src/services/CampaignAPI";
import { checkCampaignExists } from "src/services/EngagementAPIs";
import { handleChatFallbacks } from "src/utils/chatFallbacks";
import { campaigns, createTriggers, menuItems, CampaignKey } from "src/utils/constants";
import type { ConversationStep } from "../hooks/useChatLogic";
import { getActiveChatSessionId, storeChat } from "../services/ChatbotAPI";
import { message} from "antd";
import {Category} from "../components/CategoryStep";
import i18n from "src/i18n/config";
import { translateCampaignLabel } from "src/utils/campaignLabels";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { getAudienceListDisplayName } from "src/utils/audienceListLabels";

const t = (key: string, options?: Record<string, unknown>) =>
  i18n.t(`chatScreen.${key}`, options);

const ACTION_DISPLAY_KEYS: Record<string, string> = {
  "Create New Campaign": "createNewCampaign",
  "Create New List": "quickActionCreateListTitle",
  "Show campaign performance": "showCampaignPerformance",
  "View reports": "viewReports",
  Yes: "yes",
  Edit: "edit",
  "Send Now": "sendNow",
  Schedule: "schedule",
};

const getDisplayText = (rawText: string) => {
  const key = ACTION_DISPLAY_KEYS[rawText];
  if (key) {return t(key);}
  // Audience lists travel through the pipeline as their canonical English name,
  // so the label has to be looked up before echoing the choice back to the user.
  const audienceLabel = getAudienceListDisplayName(rawText);
  if (audienceLabel !== rawText) {return audienceLabel;}
  return translateCampaignLabel(rawText);
};

let sessionInitPromise: Promise<number> | null = null;

const MIME_TYPES_BY_EXTENSION: Record<string, string> = {
  csv: "text/csv",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  pdf: "application/pdf",
  png: "image/png",
  webp: "image/webp",
};

interface MessageHandlerParams {
  textToSend: string;
  conversationStep: ConversationStep;
  input: string;
  selectedFile: File | File[] | null;
  previewURL: string | null;
  campaignName: string;
  audience: string;
  generatedHtml: string | null;
  generatedPreviewUrl: string | null;
  listCreationMode: "standalone" | "campaign" | null;
  user_id: string | null;
  tenant_id: string;
  clearedRef: React.MutableRefObject<boolean>;
  navigate: (path: string, state?: any) => void;
  setQuery: React.Dispatch<React.SetStateAction<Message[]>>;
  setInput: (value: string) => void;
  setConversationStep: (step: ConversationStep) => void;
  setCampaignName: (name: string) => void;
  setAudience: (audience: string) => void;
  setListCreationMode: (mode: "standalone" | "campaign" | null) => void;
  setLoading: (loading: boolean) => void;
  setIsFetching: (fetching: boolean) => void;
  setFirstMessageSent: (sent: boolean) => void;
  setSuggestedQuestions: (questions: string[]) => void;
  setSelectedCampaign: (campaign: CampaignKey | null) => void;
  setSelectedSubCampaign: (subCampaign: string | null) => void;
  triggerAutoFetchCategories: () => Promise<void>;
  setSelectedCategories: React.Dispatch<React.SetStateAction<Category[]>>;
setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
templateJSON: any;
setTemplateJSON: (val: any) => void;
  onQueryLimitReached?: (backendDetail: string) => void;
}

export const createMessageHandler = (params: MessageHandlerParams) => {
  const {
    textToSend,
    conversationStep,
    input,
    selectedFile,
    previewURL,
    campaignName,
    audience,
    generatedHtml,
    generatedPreviewUrl,
    templateJSON,
  setTemplateJSON,
    user_id,
    tenant_id,
    clearedRef,
    navigate,
    setQuery,
    setInput,
    setConversationStep,
    setCampaignName,
    setAudience,
    setListCreationMode,
    setLoading,
    setIsFetching,
    setFirstMessageSent,
    setSuggestedQuestions,
    setSelectedCampaign,
    setSelectedSubCampaign,
    triggerAutoFetchCategories,
    onQueryLimitReached,
  } = params;

  const handleQueryLimitError = (error: unknown): boolean => {
    if (!(error instanceof ChatbotQueryLimitReachedError)) {
      return false;
    }
    onQueryLimitReached?.(error.backendDetail);
    return true;
  };

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const buildFilePayload = async () => {
    const file = Array.isArray(selectedFile) ? selectedFile[0] : selectedFile;
    const ext = file?.name.split(".").pop()?.toLowerCase();
    const isSupportedFile =
      !!file &&
      (file.type.startsWith("image/") ||
        ["png", "jpg", "jpeg", "pdf", "csv"].includes(ext || "") ||
        ["application/pdf", "text/csv", "application/csv", "application/vnd.ms-excel"].includes(file.type));
    if (!isSupportedFile) {return null;}

    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (!dataUrl) {return null;}
      const fileType =
        file.type ||
        (ext ? MIME_TYPES_BY_EXTENSION[ext] : undefined) ||
        "application/octet-stream";
      return { name: file.name, type: fileType, dataUrl };
    } catch (error) {
      return null;
    }
  };

  const persistChat = async (
    messageText: string,
    responseText: string,
    sessionHeadlineOverride?: string,
    filePayload?: { name: string; type: string; dataUrl: string } | null
  ) => {
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage && !filePayload) {return;}
    if (!responseText.trim()) {return;}

    const resolvedFilePayload =
      filePayload === undefined ? await buildFilePayload() : filePayload;
    const savedUserId = Number(localStorage.getItem("user_id")) || 621;
    const currentSessionId = Number(getActiveChatSessionId()) || 0;
    const sessionHeadline =
      sessionHeadlineOverride?.trim() || campaignName?.trim() || "AI Chat Session";

    if (currentSessionId === 0) {
      if (sessionInitPromise) {
        sessionInitPromise
          .then(sessionId => {
            if (!sessionId) {return;}
            return storeChat({
              user_id: savedUserId,
              session_id: sessionId,
              message: messageText,
              response: responseText,
              session_headline: sessionHeadline,
              lang: getApiLangFromI18n(i18n),
              file_data: resolvedFilePayload?.dataUrl,
              file_name: resolvedFilePayload?.name,
              file_type: resolvedFilePayload?.type,
            });
          })
          .catch(() => {
            message.error("Unable to save chat history right now.");
          });
        return;
      }

      sessionInitPromise = storeChat({
        user_id: savedUserId,
        session_id: 0,
        message: messageText,
        response: responseText,
        session_headline: sessionHeadline,
        lang: getApiLangFromI18n(i18n),
        file_data: resolvedFilePayload?.dataUrl,
        file_name: resolvedFilePayload?.name,
        file_type: resolvedFilePayload?.type,
      })
        .then((res: any) => {
          const newSessionId = Number(res?.session_id) || 0;
          if (newSessionId) {
            localStorage.setItem("session_id", String(newSessionId));
          }
          return newSessionId;
        })
        .catch(() => {
          message.error("Unable to save chat history right now.");
          return 0;
        })
        .finally(() => {
          sessionInitPromise = null;
        });
      return;
    }

    storeChat({
      user_id: savedUserId,
      session_id: currentSessionId,
      message: messageText,
      response: responseText,
      session_headline: sessionHeadline,
      lang: getApiLangFromI18n(i18n),
      file_data: resolvedFilePayload?.dataUrl,
      file_name: resolvedFilePayload?.name,
      file_type: resolvedFilePayload?.type,
    })
      .catch(() => {
        message.error("Unable to save chat history right now.");
      });
  };

  return async (messageText?: string) => {
    const textToProcess = messageText ?? textToSend ?? "";
    const filePayload = await buildFilePayload();
    const hasFilePayload = !!filePayload;
    if (!textToProcess.trim() && !hasFilePayload) {return;}

    const fallbackResponse = textToProcess.trim()
      ? handleChatFallbacks(textToProcess)
      : null;
    if (fallbackResponse) {
      const fallbackText = fallbackResponse
        .map(item => item.text)
        .filter(Boolean)
        .join("\n");
      const userMessage: Message = {
        sender: "user",
        text: textToProcess,
        timestamp: getTime(),
        type: "userMessage",
      };
      setQuery(prev => [...prev, userMessage, ...fallbackResponse]);
      setInput("");
      persistChat(getDisplayText(textToProcess), fallbackText, undefined, filePayload);
      return;
    }


    const campaignKeywords = ["campaign", "create", "حملة", "إنشاء", "انشاء"];
    const createCampaignSteps = [
      "askName",
      "askAudience",
      "autoFetchCategories",
      "fetchCategories",
      "selectCategory",
      "selectCampaign",
      "selectSubCampaign",
      "confirmSend",
      "sendOption",
      "impressionsStep",
      "conversionsStep",
      "roasStep",
      "engagementStep",
      "fetchingCategories",
      "handleGenerate",
    ];

    if (
      input &&
      campaignKeywords?.some(word => input.trim().toLowerCase() === word.toLowerCase()) &&
      !createCampaignSteps.includes(conversationStep ?? "")
    ) {
      const botMessage: Message & { type?: string; options?: string[] } = {
        sender: "bot",
        text: t("confirmCreateCampaignQuestion"),
        timestamp: getTime(),
        type: "confirmCampaign",
        options: ["Yes", "No"],
      };
      setQuery(prev => [...prev, botMessage]);
      setConversationStep("confirmCampaign");
      setInput("");
      persistChat(getDisplayText(textToProcess), botMessage.text || "", undefined, filePayload);
      return;
    }

   const userMessage: Message = {
  sender: "user",
  text: getDisplayText(textToProcess),
  timestamp: getTime(),
  ...(selectedFile && previewURL && {
    file: {
      name: Array.isArray(selectedFile) ? selectedFile[0].name : selectedFile.name,
      type: Array.isArray(selectedFile) ? selectedFile[0].type : selectedFile.type,
      url: previewURL,
    }
  })
};
    setQuery(prev => [...prev, userMessage]);
    setInput("");

    if (!textToProcess.trim() && filePayload) {
      setLoading(true);
      setIsFetching(true);
      try {
        const { botMessage, chatName, messageId } = await sendImageMessageToAPI(
          textToProcess,
          user_id,
          tenant_id,
          filePayload,
          getTime,
          getApiLangFromI18n(i18n)
        );

        if (clearedRef.current) {return;}

        const responseText = botMessage?.text?.trim() || "No response text found.";
        const messageToAdd: Message = {
          sender: "bot",
          text: responseText,
          type: "botMessage",
          timestamp: getTime(),
        };

        setQuery(prev => {
          if (clearedRef.current) {return prev;}
          return [...prev, messageToAdd];
        });
        persistChat(getDisplayText(textToProcess), responseText, chatName || undefined, filePayload);

        if (messageId) {
          setTimeout(async () => {
            const suggestions = await fetchChatSuggestions(messageId);
            if (!clearedRef.current && suggestions.length > 0) {
              setSuggestedQuestions(suggestions);
            }
          }, 8000);
        }
        return responseText;
      } catch (error) {
        if (clearedRef.current) {return;}
        if (handleQueryLimitError(error)) {return;}
        message.error(" Error in handleSendMessage API call:");
        setQuery(prev => [
          ...prev,
          {
            sender: "bot",
            text: t("somethingWentWrongTryAgain"),
            timestamp: getTime(),
            type: "botMessage",
          },
        ]);
      } finally {
        setLoading(false);
        setIsFetching(false);
      }
      return;
    }

    if (conversationStep === "confirmSend") {
      if (textToProcess.toLowerCase() === "yes") {
        const botMessage: Message = {
          sender: "bot",
          timestamp: getTime(),
          type: "sendOption",
        };
        setQuery(prev => [...prev, botMessage]);
        setConversationStep("sendOption");
        persistChat(
          getDisplayText(textToProcess),
          "Would you like to send now or schedule it?",
          undefined,
          filePayload
        );
        return;
      }

   if (textToProcess.toLowerCase() === "edit") {
  navigate("/account/ai-review-and-send", {
    state: {
      campaignName,
      recipients: audience,

      // ALWAYS PASS BOTH
      emailHtml: generatedHtml || null,
      emailDesign: templateJSON || null,

      // Editor always starts with JSON if available
      templateType: templateJSON ? "json" : "html",

      previewUrl: generatedPreviewUrl,
      mode: "ai",
    },
  });
  persistChat(getDisplayText(textToProcess), t("openingReviewAndSendScreen"), undefined, filePayload);
  return;
}
    }

    if (conversationStep === "sendOption") {
      if (textToProcess.toLowerCase().includes("now")) {
        setQuery(prev => [
          ...prev,
          {
            sender: "bot",
            text: t("sendingCampaignNow"),
            timestamp: getTime(),
            type: "botQuestion",
          },
        ]);

        try {
          const payload = {
            campaign_name: campaignName || "Untitled Campaign",
            send_to: [audience || "default@example.com"],
           html: generatedHtml || "<p>No HTML Content</p>",
            send_at: new Date().toISOString(),
            company_name_engagements: localStorage.getItem("company_name") || "Unknown",
            lang: getApiLangFromI18n(i18n),
          };

          await sendCampaignEmail(payload);

          const sentMessage = t("campaignSentSuccess", { name: payload.campaign_name });
          setQuery(prev => [
            ...prev,
            {
              sender: "bot",
              text: sentMessage,
              timestamp: getTime(),
              type: "botQuestion",
            },
          ]);
          persistChat(
            getDisplayText(textToProcess),
            `${t("sendingCampaignNow")}\n${sentMessage}`,
            undefined,
            filePayload
          );
        } catch (error) {
          const failMessage = t("failedToSendCampaign");
          setQuery(prev => [
            ...prev,
            {
              sender: "bot",
              text: failMessage,
              timestamp: getTime(),
              type: "botQuestion",
            },
          ]);
          persistChat(
            getDisplayText(textToProcess),
            `${t("sendingCampaignNow")}\n${failMessage}`,
            undefined,
            filePayload
          );
        }

        setConversationStep(null);
        return;
      }

      if (textToProcess.toLowerCase().includes("schedule")) {
        const botMessage: Message = {
          sender: "bot",
          text: t("whenShouldThisBeSent"),
          timestamp: getTime(),
          type: "schedulePicker",
        };
        setQuery(prev => [...prev, botMessage]);
        setConversationStep("schedulePicker");
        persistChat(getDisplayText(textToProcess), botMessage.text || "", undefined, filePayload);
        return;
      }
    }

    if (textToProcess.toLowerCase().includes("good to go")) {
      const confirmMessage: Message = {
        sender: "bot",
        text: t("redirectingNow"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, confirmMessage]);
      persistChat(getDisplayText(textToProcess), confirmMessage.text || "", undefined, filePayload);

      setTimeout(() => {
       navigate("/account/ai-review-and-send", {
  state: {
    campaignName,
    recipients: audience,
    emailHtml: generatedHtml || null,
    previewUrl: generatedPreviewUrl,
    emailDesign: templateJSON || null,
    templateType: templateJSON ? "json" : "html",
  },
});
      }, 1000);
      return;
    }

    const menu = menuItems.find(m => m.text === textToProcess);
    if (menu) {
      const botMessage: Message = {
        sender: "bot",
        timestamp: getTime(),
        type: "menuQuestions",
        menu,
      };

      setQuery(prev => {
        const updated = [...prev];
        if (
          updated.length > 0 &&
          (updated[updated.length - 1].type === "menuQuestions" ||
            updated[updated.length - 1].type === "campaignOptions")
        ) {
          updated.pop();
        }
        return [...updated, botMessage];
      });
      setSuggestedQuestions([]);
      persistChat(
        textToProcess,
        t("relatedQuestions"),
        undefined,
        filePayload
      );
      return;
    }

    const normalizedText = textToProcess.trim().toLowerCase();
    if (createTriggers.some(trigger => normalizedText.includes(trigger))) {
      const userMsg: Message = {
        sender: "user",
        timestamp: getTime(),
        type: "userMessage",
      };
      setQuery(prev => [...prev, userMsg]);

      const botMessage: Message = {
        sender: "bot",
        text: t("createCampaignReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);
      setConversationStep("askName");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      persistChat(getDisplayText(textToProcess), botMessage.text || "", undefined, filePayload);
      return;
    }

    const trimmedInput = textToProcess.trim().toLowerCase();
    if (
      ["create", "campaign", "حملة", "إنشاء", "انشاء"].includes(trimmedInput) &&
      !createCampaignSteps.includes(conversationStep ?? "")
    ) {
      const userMsg: Message = {
        sender: "user",
        text: textToProcess,
        timestamp: getTime(),
        type: "userMessage",
      };
      const botMessage: Message & { type?: string; options?: string[] } = {
        sender: "bot",
        text: t("confirmCreateCampaignQuestion"),
        timestamp: getTime(),
        type: "confirmCampaign",
        options: ["Yes", "No"],
      };
      setQuery(prev => [...prev, userMsg, botMessage]);
      setConversationStep("confirmCampaign");
      setInput("");
      persistChat(getDisplayText(textToProcess), botMessage.text || "", undefined, filePayload);
      return;
    }

    if (conversationStep === "askName") {
      const campaign = textToProcess.trim();
      setCampaignName(campaign);
      const tenantId = parseInt(localStorage?.getItem("tenant_id") || "0", 10);

      // Let the user know we're checking the name with the backend
      const checkingMessage: Message = {
        sender: "bot",
        text: t("checkingCampaignNameAvailability", { name: campaign }),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, checkingMessage]);

      try {
        const response = await checkCampaignExists({
          tenant_id: tenantId,
          campaign_name: campaign,
        });

        const exists = (response.data as any)?.exists ?? false;

        if (exists) {
          const botMessage: Message = {
            sender: "bot",
            text: t("campaignNameAlreadyExists", { name: campaign }),
            timestamp: getTime(),
            type: "botQuestion",
          };
          setQuery(prev => [...prev, botMessage]);
          setConversationStep("askName");
          persistChat(getDisplayText(textToProcess), botMessage.text || "", campaign, filePayload);
          return;
        } else {
          const botMessage: Message = {
            sender: "bot",
            text: t("selectExistingOrCreateList"),
            timestamp: getTime(),
            type: "botQuestion",
          };
          setQuery(prev => [...prev, botMessage]);
          setConversationStep("askAudience");
          persistChat(getDisplayText(textToProcess), botMessage.text || "", campaign, filePayload);
          return;
        }
      } catch (error) {
        message.error(t("campaignNameAlreadyExistsError"));
        const errorMessage: Message = {
          sender: "bot",
          text: t("campaignNameAlreadyExistsError"),
          timestamp: getTime(),
          type: "botQuestion",
        };
        setQuery(prev => [...prev, errorMessage]);
        setConversationStep("askName");
        persistChat(getDisplayText(textToProcess), errorMessage.text || "", campaign, filePayload);
        return;
      }
    }

    if (conversationStep === "askAudience") {
      const selectedAudience = textToProcess.trim();
      setAudience(selectedAudience);

      if (
        !selectedAudience.toLowerCase().includes("create") &&
        !selectedAudience.includes("إنشاء") &&
        !selectedAudience.includes("انشاء")
      ) {
        setListCreationMode("campaign");
        const botMessage: Message = {
          sender: "bot",
          text: t("fetchingCategoriesForAudience", {
            audience: getAudienceListDisplayName(selectedAudience),
          }),
          timestamp: getTime(),
          type: "botQuestion",
        };
        setQuery(prev => [...prev, botMessage]);
        setConversationStep("autoFetchCategories");
        persistChat(getDisplayText(textToProcess), botMessage.text || "", undefined, filePayload);
        await triggerAutoFetchCategories();
        return;
      }

      setConversationStep(null);
    }

    if (conversationStep === "selectCategory") {
      const botMessage: Message = {
        sender: "bot",
        text: t("greatSelectCampaignCategory"),
        timestamp: getTime(),
        type: "campaignOptions",
        options: campaigns,
      };
      setQuery(prev => [...prev, botMessage]);
      setConversationStep("selectCampaign");
      persistChat(getDisplayText(textToProcess), botMessage.text || "", undefined, filePayload);
      return;
    }

    if (conversationStep === "selectCampaign") {
      const campaignKey = textToProcess as CampaignKey;
      setSelectedCampaign(campaignKey);

      if (campaignKey === "Custom") {
        const botMessage: Message = {
          sender: "bot",
          text: t("customCampaignFormPrompt"),
          timestamp: getTime(),
          type: "customCampaignForm",
        };
        setQuery(prev => [...prev, botMessage]);
        setConversationStep("customCampaignForm");
        // `textToProcess` doubles as the lookup key for `campaigns`/
        // `subCampaigns` (English-keyed), so it must stay untouched for
        // state; what gets persisted is the label the user actually saw.
        persistChat(translateCampaignLabel(textToProcess), botMessage.text || "", undefined, filePayload);
      } else {
        const botMessage: Message = {
          sender: "bot",
          text: t("nowSelectSubCampaign"),
          timestamp: getTime(),
          type: "subCampaignOptions",
        };
        setQuery(prev => [...prev, botMessage]);
        setConversationStep("selectSubCampaign");
        persistChat(translateCampaignLabel(textToProcess), botMessage.text || "", undefined, filePayload);
      }
      return;
    }

    if (conversationStep === "selectSubCampaign") {
      setSelectedSubCampaign(textToProcess);

      const botMessage: Message = {
        sender: "bot",
        text: t("subCampaignSelectedConfirmation", {
          subCampaign: translateCampaignLabel(textToProcess),
          campaignName,
        }),
        timestamp: getTime(),
        type: "botQuestion",
      };

      const promptMessage: Message = {
        sender: "bot",
        timestamp: getTime(),
        type: "promptStep",
      };

      setQuery(prev => [...prev, botMessage, promptMessage]);
      setConversationStep("fillPrompt");
      persistChat(translateCampaignLabel(textToProcess), botMessage.text || "", undefined, filePayload);
      return;
    }

    setLoading(true);
    setIsFetching(true);
    try {
      // The backend classifies intent off this exact text (see the
      // "Create New Campaign" quick action below) — it must stay the raw
      // canonical English trigger, NOT the localized display text. Confirmed
      // by regression: sending the Arabic translation of "Create New
      // Campaign" here made the backend stop returning `intent: "engagement"`,
      // so the UI fell through to a plain LLM reply instead of starting the
      // campaign wizard. `getDisplayText` is used only for what gets written
      // to the `store-chat` log below, which doesn't drive any behavior.
      const { botMessage, chatName, messageId, intent } = filePayload
        ? await sendImageMessageToAPI(
            textToProcess,
            user_id,
            tenant_id,
            filePayload,
            getTime,
            getApiLangFromI18n(i18n)
          )
        : await sendMessageToAPI(textToProcess, user_id, tenant_id, getTime, getApiLangFromI18n(i18n));

      if (clearedRef.current) {return;}

      // Intent-driven UI: the backend classifies the message and returns an
      // `intent`. When it signals campaign creation ("engagement"), suppress the
      // text reply and launch the campaign wizard UI instead of showing it.
      if (intent?.includes("engagement")) {
        const introMessage: Message = {
          sender: "bot",
          text: t("createCampaignReply"),
          timestamp: getTime(),
          type: "botQuestion",
        };
        setQuery(prev => {
          if (clearedRef.current) {return prev;}
          return [...prev, introMessage];
        });
        setConversationStep("askName");
        setFirstMessageSent(true);
        setSuggestedQuestions([]);
        persistChat(getDisplayText(textToProcess), introMessage.text || "", chatName || undefined, filePayload);
        return introMessage.text;
      }

      const responseText = botMessage?.text?.trim() || "No response text found.";
      const messageToAdd: Message = {
        sender: "bot",
        text: responseText,
        type: "botMessage",
        timestamp: getTime(),
      };

      setQuery(prev => {
        if (clearedRef.current) {return prev;}
        return [...prev, messageToAdd];
      });
try {
  persistChat(getDisplayText(textToProcess), responseText, chatName || undefined, filePayload);
} catch (error) {
  message.error("Unexpected storeChat wrapper error:");
}

      if (
        conversationStep === "roasStep" ||
        conversationStep === "impressionsStep" ||
        conversationStep === "conversionsStep" ||
        conversationStep === "engagementStep"
      ) {
        setConversationStep(null);
      }

      if (messageId) {
        setTimeout(async () => {
          const suggestions = await fetchChatSuggestions(messageId);
          if (!clearedRef.current && suggestions.length > 0) {
            setSuggestedQuestions(suggestions);
          }
        }, 8000);
      }
          return responseText;
    } catch (error) {
      if (clearedRef.current) {return;}
      if (handleQueryLimitError(error)) {return;}
      message.error(" Error in handleSendMessage API call:");
      setQuery(prev => [
        ...prev,
        {
          sender: "bot",
          text: t("somethingWentWrongTryAgain"),
          timestamp: getTime(),
          type: "botMessage",
        },
      ]);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }

  };
};
